import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Crown, Check, Bell, Star, BookmarkSimple, Sparkle, ArrowRight, CalendarBlank } from "@phosphor-icons/react";

const PREMIUM_BENEFITS = [
  { icon: Bell, title: "Alert eventi", desc: "Notifiche via email quando nuovi eventi corrispondono ai tuoi filtri, marche e categorie preferite." },
  { icon: BookmarkSimple, title: "Salvataggi illimitati", desc: "Salva quanti eventi vuoi nel tuo garage personale. Costruisci la tua stagione dei raduni." },
  { icon: Star, title: "Accesso anticipato", desc: "Vedi gli eventi featured 24 ore prima di tutti gli altri, prima che si riempiano." },
  { icon: Sparkle, title: "Ricerca AI potenziata", desc: "Query naturali illimitate con Claude Sonnet e cronologia delle ricerche salvata." },
  { icon: CalendarBlank, title: "Export calendario", desc: "Esporta i tuoi eventi salvati in Google Calendar, Apple Calendar e iCal." },
];

export default function Premium() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPkg(data.premium)).catch(() => {});
  }, []);

  const isPremium = user?.is_premium;
  const premiumUntil = user?.premium_until;

  const handleSubscribe = async () => {
    if (!user) {
      toast.error("Accedi per abbonarti");
      navigate("/login");
      return;
    }
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/payments/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore checkout");
      setCheckoutLoading(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-black to-black" />
        <div className="grain-overlay" />
        <div className="relative max-w-[1600px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-primary" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary flex items-center gap-2">
              <Crown size={14} weight="fill" /> WheelsMeet Premium
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tighter max-w-4xl mb-6" data-testid="premium-title">
            Il tuo <span className="text-primary">garage</span> potenziato
          </h1>
          <p className="max-w-2xl text-base sm:text-lg text-neutral-300 leading-relaxed">
            Non perdere mai un raduno per le tue auto e moto preferite. Alert intelligenti, salvataggi illimitati, accesso anticipato agli eventi più esclusivi.
          </p>
        </div>
      </section>

      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-3 gap-12">
        {/* Benefits */}
        <div className="lg:col-span-2">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4">Cosa include</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tighter mb-10">Tutti i vantaggi Premium</h2>
          <div className="space-y-6">
            {PREMIUM_BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="border border-border rounded-sm p-6 bg-card flex gap-5 fade-in-up" style={{ animationDelay: `${i * 60}ms` }}>
                  <div className="w-12 h-12 shrink-0 bg-primary/10 border border-primary/30 flex items-center justify-center rounded-sm">
                    <Icon size={22} weight="fill" className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg tracking-tight mb-1">{b.title}</h3>
                    <p className="text-sm text-neutral-400 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pricing card */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border-2 border-primary rounded-sm p-8 bg-gradient-to-br from-primary/10 to-transparent">
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-primary mb-4 flex items-center gap-2">
              <Crown size={12} weight="fill" /> Premium
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <div className="font-display text-6xl font-black tracking-tighter">€{pkg?.amount?.toFixed(2) || "4.99"}</div>
              <div className="text-sm text-neutral-400 font-semibold">/ 30 giorni</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-6">Rinnovabile · disdici quando vuoi</div>

            <ul className="space-y-2 mb-8">
              {["Alert email personalizzati", "Salvataggi illimitati", "Accesso anticipato", "AI search illimitata", "Export calendario"].map(l => (
                <li key={l} className="flex items-center gap-2 text-sm text-neutral-200">
                  <Check size={14} weight="bold" className="text-primary shrink-0" /> {l}
                </li>
              ))}
            </ul>

            {isPremium ? (
              <div className="text-center py-3 border border-primary/30 rounded-sm bg-primary/5">
                <div className="text-xs font-bold uppercase tracking-widest text-primary mb-1">Sei Premium</div>
                {premiumUntil && (
                  <div className="text-[10px] text-neutral-400">
                    Attivo fino al {new Date(premiumUntil).toLocaleDateString("it-IT")}
                  </div>
                )}
                <Button onClick={handleSubscribe} disabled={checkoutLoading} className="w-full mt-4 h-12 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm" data-testid="premium-extend-btn">
                  {checkoutLoading ? "..." : "Estendi 30 giorni"}
                </Button>
              </div>
            ) : (
              <Button onClick={handleSubscribe} disabled={checkoutLoading || loading} data-testid="premium-subscribe-btn" className="w-full h-14 bg-primary hover:bg-primary/90 text-white font-black uppercase tracking-wider rounded-sm">
                {checkoutLoading ? "Reindirizzamento..." : <>Diventa Premium <ArrowRight size={16} weight="bold" className="ml-2" /></>}
              </Button>
            )}
            <div className="mt-4 text-[10px] text-neutral-500 text-center">
              Pagamento sicuro con Stripe · Test: 4242 4242 4242 4242
            </div>
          </div>

          {!user && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-xs uppercase tracking-widest text-neutral-400 hover:text-primary">Non hai un account? Accedi →</Link>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
